/**
 * ShiftRoster.js
 *
 * BUG FIX (this version): date helpers mondayOf(), todayStr(), and
 * addDaysStr() previously built date strings via `.toISOString().slice(0,10)`
 * on Date objects constructed from local-time strings like
 * `new Date(dateStr + 'T00:00:00')`. Because that string has no timezone
 * suffix, JS parses it as LOCAL midnight — but .toISOString() always
 * converts to UTC first. For any positive UTC offset (e.g. IST, UTC+5:30,
 * PeoplesDock's primary market), local midnight lands at 18:30 the
 * PREVIOUS day in UTC, so every date string produced this way was
 * silently shifted back by one calendar day. This is exactly why "Mon 23
 * Jun" was displayed when June 23, 2026 is actually a Tuesday — the
 * whole week's date grid was off by one.
 *
 * It also explains why clicking a roster cell sent a date one day off
 * from what was visually shown, since the corrupted weekStart value fed
 * directly into the API call and the resulting day.date values.
 *
 * FIXED: all three helpers now build YYYY-MM-DD strings manually from
 * LOCAL date components (getFullYear/getMonth/getDate) — the same safe
 * pattern already used correctly elsewhere in this codebase (see
 * localToday() in attendance.controller.js). No round-trip through UTC
 * at any point, so the displayed date and the day-of-week label can
 * never disagree again, regardless of the browser's timezone.
 *
 * FEATURE 5 — Day-specific Shift Roster
 *
 * A weekly calendar grid: employees as rows, Mon–Sun as columns. Each cell
 * shows which shift the employee works that day. Clicking a cell opens an
 * inline picker to set a day-specific override (or clear back to default).
 *
 * Complements ShiftManagement.js (which defines shifts and sets each
 * employee's long-term DEFAULT shift). This page handles day-to-day
 * rotation on top of that default — essential for manufacturing, retail,
 * and BPO teams with rotating shifts.
 *
 * Calls (backend module — shift-roster.routes.js):
 *   GET    /shifts/roster/week?start_date=&department_id=
 *   POST   /shifts/roster/assign           { employee_id, date, shift_id }
 *   POST   /shifts/roster/bulk-assign      { employee_ids, dates, shift_id }
 *   DELETE /shifts/roster/:employeeId/:date
 *   POST   /shifts/roster/copy-week        { from_start_date, to_start_date }
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal,
    Form, Spinner, OverlayTrigger, Popover,
} from 'react-bootstrap';
import {
    Calendar, ChevronLeft, ChevronRight, Copy,
    Users, RefreshCw, X, CheckCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHIFT_COLORS = [
    { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
    { bg: '#f0fdf4', text: '#059669', border: '#86efac' },
    { bg: '#fff7ed', text: '#d97706', border: '#fed7aa' },
    { bg: '#fdf2f8', text: '#db2777', border: '#fbcfe8' },
    { bg: '#f0f9ff', text: '#0284c7', border: '#bae6fd' },
    { bg: '#faf5ff', text: '#9333ea', border: '#e9d5ff' },
];
const colorFor = (shiftId, shiftIdsInOrder) => {
    if (!shiftId) return null;
    const idx = shiftIdsInOrder.indexOf(shiftId);
    return SHIFT_COLORS[idx % SHIFT_COLORS.length];
};

// ── Date helpers (FIXED — no UTC round-trip, all LOCAL components) ────────
// Build YYYY-MM-DD from a Date object's LOCAL components — never via
// toISOString(), which always converts to UTC and can shift the date
// backward by one day for any positive UTC offset timezone.
const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mondayOf = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDay(); // 0=Sun..6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return toLocalDateStr(d);
};
const todayStr = () => toLocalDateStr(new Date());
const addDaysStr = (dateStr, days) => {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return toLocalDateStr(d);
};
const fmtDayDate = (dateStr) => new Date(dateStr + 'T00:00:00').getDate();
const fmtMonth = (dateStr) => new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' });
const fmtRange = (start, end) => {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    const sameMonth = s.getMonth() === e.getMonth();
    const startStr = s.toLocaleDateString('en-IN', { day: 'numeric', month: sameMonth ? undefined : 'short' });
    const endStr    = e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} – ${endStr}`;
};
// ── Inline cell shift picker (popover content) ─────────────────────────────
const CellPicker = ({ shifts, currentShiftId, isOverride, onPick, onClear }) => (
    <div style={{ padding: 8, minWidth: 180 }}>
        <div className="d-flex flex-column gap-1">
            <button
                type="button"
                className="btn btn-sm text-start"
                style={{ background: !currentShiftId ? '#f1f5f9' : 'transparent', border: 'none', fontSize: 13 }}
                onClick={() => onPick(null)}>
                — No shift (day off)
            </button>
            {shifts.map(s => (
                <button
                    key={s.id}
                    type="button"
                    className="btn btn-sm text-start"
                    style={{
                        background: currentShiftId === s.id ? '#eef2ff' : 'transparent',
                        border: 'none', fontSize: 13,
                        fontWeight: currentShiftId === s.id ? 600 : 400,
                    }}
                    onClick={() => onPick(s.id)}>
                    {s.name}
                    <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                        {s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}
                    </span>
                </button>
            ))}
            {isOverride && (
                <>
                    <hr className="my-1" />
                    <button
                        type="button"
                        className="btn btn-sm text-start text-danger"
                        style={{ border: 'none', fontSize: 12 }}
                        onClick={onClear}>
                        <X size={12} className="me-1" />Clear override (use default)
                    </button>
                </>
            )}
        </div>
    </div>
);
// ── Bulk Assign Modal ───────────────────────────────────────────────────────
const BulkAssignModal = ({ show, onHide, employees, shifts, weekDates, onDone }) => {
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedDays,      setSelectedDays]      = useState([0, 1, 2, 3, 4]);
    const [shiftId,           setShiftId]           = useState('');
    const [saving,            setSaving]            = useState(false);
    useEffect(() => {
        if (show) {
            setSelectedEmployees([]);
            setSelectedDays([0, 1, 2, 3, 4]);
            setShiftId('');
        }
    }, [show]);
    const toggleEmployee = (id) => {
        setSelectedEmployees(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
    };
    const toggleDay = (idx) => {
        setSelectedDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort());
    };
    const selectAllEmployees = () => setSelectedEmployees(employees.map(e => e.employee_id));
    const submit = async () => {
        if (!selectedEmployees.length) return toast.error('Select at least one employee');
        if (!selectedDays.length) return toast.error('Select at least one day');
        setSaving(true);
        try {
            const dates = selectedDays.map(idx => weekDates[idx]);
            await axiosInstance.post('/shifts/roster/bulk-assign', {
                employee_ids: selectedEmployees,
                dates,
                shift_id: shiftId || null,
            });
            toast.success(`Roster updated for ${selectedEmployees.length} employee(s)`);
            onDone();
            onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Bulk assign failed');
        } finally { setSaving(false); }
    };
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <Users size={18} className="me-2 text-primary" />Bulk Assign Shift
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-4">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Shift</Form.Label>
                        <Form.Select value={shiftId} onChange={e => setShiftId(e.target.value)}>
                            <option value="">— No shift (day off) —</option>
                            {shifts.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.name} ({s.start_time?.slice(0,5)}–{s.end_time?.slice(0,5)})
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold mb-2">Days</Form.Label>
                        <div className="d-flex gap-2 flex-wrap">
                            {DAY_LABELS.map((label, idx) => (
                                <button key={idx} type="button"
                                    onClick={() => toggleDay(idx)}
                                    style={{
                                        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                        cursor: 'pointer',
                                        border: `1.5px solid ${selectedDays.includes(idx) ? '#4f46e5' : '#e2e8f0'}`,
                                        background: selectedDays.includes(idx) ? '#eef2ff' : '#fff',
                                        color: selectedDays.includes(idx) ? '#4f46e5' : '#94a3b8',
                                    }}>
                                    {label} {weekDates[idx] ? fmtDayDate(weekDates[idx]) : ''}
                                </button>
                            ))}
                        </div>
                    </Col>
                    <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="small fw-bold mb-0">
                                Employees ({selectedEmployees.length} selected)
                            </Form.Label>
                            <Button size="sm" variant="link" className="p-0" onClick={selectAllEmployees}>
                                Select all
                            </Button>
                        </div>
                        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                            {employees.map(emp => (
                                <div key={emp.employee_id}
                                    className="d-flex align-items-center gap-2 px-3 py-2"
                                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                    onClick={() => toggleEmployee(emp.employee_id)}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectedEmployees.includes(emp.employee_id)}
                                        onChange={() => toggleEmployee(emp.employee_id)}
                                        className="mb-0"
                                    />
                                    <div className="small">
                                        <span className="fw-medium">{emp.employee_name}</span>
                                        {emp.department_name && (
                                            <span className="text-muted ms-2">{emp.department_name}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={submit} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : `Apply to ${selectedEmployees.length} Employee(s)`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
const ShiftRoster = () => {
    const [weekStart,   setWeekStart]   = useState(mondayOf(todayStr()));
    const [departments, setDepartments] = useState([]);
    const [deptFilter,  setDeptFilter]  = useState('');
    const [data,        setData]        = useState({ dates: [], employees: [], shifts: [] });
    const [loading,     setLoading]     = useState(true);
    const [showBulk,    setShowBulk]    = useState(false);
    const [copying,     setCopying]     = useState(false);
    const weekDates = data.dates?.length === 7 ? data.dates : Array.from({ length: 7 }, (_, i) => addDaysStr(weekStart, i));
    const weekEnd    = weekDates[6];
    const load = useCallback(() => {
        setLoading(true);
        axiosInstance.get('/shifts/roster/week', {
            params: { start_date: weekStart, department_id: deptFilter || undefined },
        })
            .then(r => setData(r.data.data || { dates: [], employees: [], shifts: [] }))
            .catch(() => toast.error('Failed to load roster'))
            .finally(() => setLoading(false));
    }, [weekStart, deptFilter]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        axiosInstance.get('/hr/departments').then(r => setDepartments(r.data.data || [])).catch(() => {});
    }, []);
    const shiftIdsInOrder = useMemo(() => data.shifts.map(s => s.id), [data.shifts]);
    const prevWeek = () => setWeekStart(w => addDaysStr(w, -7));
    const nextWeek = () => setWeekStart(w => addDaysStr(w, 7));
    const thisWeek = () => setWeekStart(mondayOf(todayStr()));
    const assignCell = async (employeeId, date, shiftId) => {
        try {
            await axiosInstance.post('/shifts/roster/assign', { employee_id: employeeId, date, shift_id: shiftId });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update roster');
        }
    };
    const clearCell = async (employeeId, date) => {
        try {
            await axiosInstance.delete(`/shifts/roster/${employeeId}/${date}`);
            load();
        } catch {
            toast.error('Failed to clear override');
        }
    };
    const copyToNextWeek = async () => {
        setCopying(true);
        try {
            const nextWeekStart = addDaysStr(weekStart, 7);
            const res = await axiosInstance.post('/shifts/roster/copy-week', {
                from_start_date: weekStart,
                to_start_date:   nextWeekStart,
            });
            toast.success(res.data.message || 'Roster copied to next week');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Copy failed');
        } finally { setCopying(false); }
    };
    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Calendar size={22} className="me-2 text-primary" />Shift Roster
                    </h4>
                    <p className="text-muted small mb-0">
                        Assign day-specific shifts. Overrides the employee's default shift for that day only.
                    </p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline-primary" onClick={() => setShowBulk(true)}>
                        <Users size={14} className="me-1" />Bulk Assign
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={copyToNextWeek} disabled={copying}>
                        {copying ? <Spinner size="sm" className="me-1" /> : <Copy size={14} className="me-1" />}
                        Copy to Next Week
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={load}>
                        <RefreshCw size={14} />
                    </Button>
                </div>
            </div>
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-3 d-flex align-items-center gap-3 flex-wrap">
                    <Button size="sm" variant="outline-secondary" onClick={prevWeek}>
                        <ChevronLeft size={16} />
                    </Button>
                    <div className="fw-bold" style={{ minWidth: 200, textAlign: 'center' }}>
                        {fmtRange(weekStart, weekEnd)}
                    </div>
                    <Button size="sm" variant="outline-secondary" onClick={nextWeek}>
                        <ChevronRight size={16} />
                    </Button>
                    <Button size="sm" variant="link" onClick={thisWeek}>This Week</Button>
                    <div className="ms-auto">
                        <Form.Select size="sm" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
                            style={{ minWidth: 180 }}>
                            <option value="">All Departments</option>
                            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Form.Select>
                    </div>
                </Card.Body>
            </Card>
            {data.shifts.length > 0 && (
                <div className="d-flex gap-3 flex-wrap mb-3">
                    {data.shifts.map(s => {
                        const c = colorFor(s.id, shiftIdsInOrder);
                        return (
                            <div key={s.id} className="d-flex align-items-center gap-1">
                                <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, border: `1px solid ${c.border}` }} />
                                <span className="small text-muted">{s.name}</span>
                            </div>
                        );
                    })}
                </div>
            )}
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : data.employees.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <Users size={40} className="text-muted mx-auto mb-3 opacity-50" />
                    <h6 className="text-muted fw-bold">No employees found</h6>
                    <p className="text-muted small mb-0">
                        {deptFilter ? 'Try a different department filter.' : 'Add active employees to start building a roster.'}
                    </p>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table mb-0 align-middle" style={{ minWidth: 900 }}>
                            <thead style={{ background: '#f8fafc' }}>
                                <tr>
                                    <th className="ps-4" style={{ minWidth: 200, fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>
                                        Employee
                                    </th>
                                    {weekDates.map((date, idx) => {
                                        const isToday = date === todayStr();
                                        return (
                                            <th key={date} className="text-center" style={{ minWidth: 100 }}>
                                                <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                                                    {DAY_LABELS[idx]}
                                                </div>
                                                <div style={{ fontSize: 14, fontWeight: 700, color: isToday ? '#4f46e5' : '#1e293b' }}>
                                                    {fmtDayDate(date)} {fmtMonth(date)}
                                                    {isToday && <Badge bg="primary" className="ms-1" style={{ fontSize: 8 }}>Today</Badge>}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {data.employees.map(emp => (
                                    <tr key={emp.employee_id}>
                                        <td className="ps-4">
                                            <div className="fw-medium small">{emp.employee_name}</div>
                                            <div className="text-muted" style={{ fontSize: 10 }}>
                                                {emp.employee_code}{emp.department_name ? ` · ${emp.department_name}` : ''}
                                            </div>
                                        </td>
                                        {emp.days.map(day => {
                                            const color = colorFor(day.shift_id, shiftIdsInOrder);
                                            return (
                                                <td key={day.date} className="text-center p-1">
                                                    <OverlayTrigger
                                                        trigger="click"
                                                        placement="bottom"
                                                        rootClose
                                                        overlay={
                                                            <Popover id={`pop-${emp.employee_id}-${day.date}`}>
                                                                <CellPicker
                                                                    shifts={data.shifts}
                                                                    currentShiftId={day.shift_id}
                                                                    isOverride={day.is_override}
                                                                    onPick={(shiftId) => assignCell(emp.employee_id, day.date, shiftId)}
                                                                    onClear={() => clearCell(emp.employee_id, day.date)}
                                                                />
                                                            </Popover>
                                                        }>
                                                        <div
                                                            role="button"
                                                            style={{
                                                                padding: '8px 6px',
                                                                borderRadius: 8,
                                                                cursor: 'pointer',
                                                                fontSize: 11,
                                                                fontWeight: day.is_override ? 700 : 500,
                                                                background: color ? color.bg : '#f8fafc',
                                                                color:      color ? color.text : '#94a3b8',
                                                                border: `1px solid ${color ? color.border : '#e2e8f0'}`,
                                                                position: 'relative',
                                                                minHeight: 36,
                                                            }}>
                                                            {day.shift_name || '—'}
                                                            {day.is_override && (
                                                                <span style={{
                                                                    position: 'absolute', top: 2, right: 4,
                                                                    width: 5, height: 5, borderRadius: '50%',
                                                                    background: color ? color.text : '#94a3b8',
                                                                }} title="Day-specific override" />
                                                            )}
                                                        </div>
                                                    </OverlayTrigger>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-3 border-top text-muted small d-flex align-items-center gap-2">
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4f46e5', display: 'inline-block' }} />
                        Dot = day-specific override · Plain = employee's default shift · Click any cell to change
                    </div>
                </Card>
            )}
            <BulkAssignModal
                show={showBulk}
                onHide={() => setShowBulk(false)}
                employees={data.employees}
                shifts={data.shifts}
                weekDates={weekDates}
                onDone={load}
            />
        </div>
    );
};
export default ShiftRoster;
